import React, { useState } from "react";
import { roleRepo, settingRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, Card, PageHead, useAsync } from "../../components/ui";

const ACTIONS = ["view", "create", "update", "delete", "use"];

// สิทธิ์ที่เก็บเป็น wildcard (เช่น "customer.*") -> กางเป็นรายการจริงก่อนแก้ จะได้ติ๊กออกทีละช่องได้
const expand = (permissions, groups) => {
  const out = new Set(permissions);
  (groups || []).forEach((group) => {
    if (out.has(`${group.key}.*`)) {
      out.delete(`${group.key}.*`);
      group.actions.forEach((action) => out.add(`${group.key}.${action}`));
    }
  });
  return out;
};

export default function Roles() {
  const { orgId, toast } = useApp();
  const { tx } = useI18n();
  const { data: roles, reload } = useAsync(() => roleRepo.forOrg(orgId), [orgId], []);
  const { data: groups } = useAsync(() => settingRepo.getPermissionGroups(), [], []);
  // draft[roleId] = Set ของสิทธิ์ที่กำลังแก้ (ยังไม่บันทึก)
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState("");

  const current = (role) => draft[role.id] || expand(role.permissions || [], groups);

  const toggle = (role, perm) => {
    const next = new Set(current(role));
    if (next.has(perm)) next.delete(perm);
    else next.add(perm);
    setDraft((prev) => ({ ...prev, [role.id]: next }));
  };

  const save = async (role) => {
    setSaving(role.id);
    try {
      await roleRepo.savePermissions(orgId, role.id, Array.from(current(role)));
      setDraft((prev) => {
        const { [role.id]: _drop, ...rest } = prev; // eslint-disable-line no-unused-vars
        return rest;
      });
      reload();
      toast(tx("บันทึกสิทธิ์เรียบร้อย", "Permissions saved"), "success");
    } catch (err) {
      toast(err.message || tx("บันทึกสิทธิ์ไม่สำเร็จ", "Failed to save permissions"), "error");
    } finally {
      setSaving("");
    }
  };

  return (
    <>
      <PageHead title={tx("บทบาท & สิทธิ์การใช้งาน", "Roles & permissions")} subtitle={tx("กำหนดสิทธิ์การเข้าถึงแต่ละฟีเจอร์ตามบทบาทของพนักงาน (แยกต่อร้าน)", "Control feature access per staff role (per shop)")} />

      <div className="grid-2">
        {(roles || []).map((role) => {
          const perms = role.isSystem ? role.permissions || [] : Array.from(current(role));
          return (
            <Card
              key={role.id}
              title={role.name}
              subtitle={role.isSystem ? tx("บทบาทระบบ แก้ไขไม่ได้", "System role, read only") : tx("กำหนดเองได้", "Customizable")}
              actions={!role.isSystem && draft[role.id] ? <Button variant="primary" size="sm" onClick={() => save(role)} disabled={saving === role.id}>{tx("บันทึก", "Save")}</Button> : null}
            >
              <div className="u-flex u-wrap u-gap-4 u-mb-16">
                {perms[0] === "*" ? (
                  <Badge tone="violet">{tx("สิทธิ์เต็มทุกอย่าง", "Full access")}</Badge>
                ) : (
                  perms.map((perm) => <Badge key={perm} tone="indigo">{perm}</Badge>)
                )}
              </div>

              {!role.isSystem ? (
                <div style={{ overflowX: "auto", maxWidth: "100%" }}>
                <table className="table-crm roles-table">
                  <thead>
                    <tr>
                      <th>{tx("โมดูล", "Module")}</th>
                      {ACTIONS.map((action) => (
                        <th key={action} className="u-center">{action}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(groups || []).map((group) => (
                      <tr key={group.key}>
                        <td>{group.name}</td>
                        {ACTIONS.map((action) => (
                          <td key={action} className="u-center">
                            {group.actions.includes(action) ? (
                              <input type="checkbox" checked={current(role).has(`${group.key}.${action}`)} onChange={() => toggle(role, `${group.key}.${action}`)} />
                            ) : (
                              "-"
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              ) : null}
            </Card>
          );
        })}
      </div>
    </>
  );
}
